import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Reveal from "../components/Reveal";
import AnimatedCounter from "../components/AnimatedCounter";
import Seo from "../components/Seo";
import { PageHero } from "./Services";
import { networks, networkProcess, networkAudience, networkStats, networkVerticals, company } from "../data/mock";

const Networks = () => {
  return (
    <div className="overflow-hidden">
      <Seo
        title="Affiliate & Pay-Per-Call Networks"
        description="Grow on a pure performance basis. Jellis Enterprise runs affiliate networks and pay-per-call campaigns — connecting advertisers with vetted partners and driving qualified, ready-to-buy phone calls."
      />
      <PageHero
        eyebrow="Networks"
        title="Performance networks that pay for results"
        subtitle="Our affiliate and pay-per-call networks connect advertisers with a vetted community of partners — so you only pay when real sales and qualified calls come through."
      />

      {/* Detailed offerings */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6 space-y-24">
          {networks.map((n, i) => (
            <Reveal key={n.slug}>
              <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}>
                <div className="relative">
                  <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-slate-200">
                    <img src={n.image} alt={n.title} className="w-full h-80 object-cover" />
                  </div>
                  <div className="absolute -bottom-5 -left-5 flex h-16 w-16 items-center justify-center rounded-2xl brand-gradient shadow-xl">
                    <n.icon className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div>
                  <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 border border-blue-100 font-semibold">{n.tag}</Badge>
                  <h2 className="mt-4 font-display text-3xl font-extrabold text-slate-900">{n.title}</h2>
                  <p className="mt-4 text-lg text-slate-600 leading-relaxed">{n.long}</p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    {n.points.map((pt) => (
                      <div key={pt} className="flex items-center gap-2.5 text-slate-700 font-medium">
                        <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0" /> {pt}
                      </div>
                    ))}
                  </div>
                  <a href={company.bookingUrl} target="_blank" rel="noreferrer">
                    <Button className="mt-8 brand-gradient text-white font-bold group">
                      Talk to our team
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="section-pad bg-slate-50">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">How it works</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Everybody wins on performance</h2>
            <p className="mt-4 text-lg text-slate-600">A simple, transparent model that rewards results at every step.</p>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {networkProcess.map((p, i) => (
              <Reveal key={p.step} delay={i * 80}>
                <div className="relative h-full rounded-2xl bg-white border border-slate-200 p-7 card-lift">
                  <span className="font-display text-5xl font-extrabold text-slate-100 absolute top-4 right-5 select-none">{p.step}</span>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <p.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Vertical showcase */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">Top verticals</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">We drive calls across dozens of verticals</h2>
            <p className="mt-4 text-lg text-slate-600">Find your fit fast — tap a vertical to talk about a custom pay-per-call campaign.</p>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {networkVerticals.map((v, i) => (
              <Reveal key={v.name} delay={(i % 4) * 60}>
                <Link
                  to={`/networks/${v.slug}`}
                  className="group flex h-full flex-col items-center text-center rounded-2xl border border-slate-200 bg-white p-6 card-lift"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
                    <v.icon className="h-7 w-7" />
                  </div>
                  <h3 className="mt-4 font-display font-bold text-slate-900">{v.name}</h3>
                  <p className="mt-1 text-sm text-slate-500">{v.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 opacity-0 transition-opacity group-hover:opacity-100">
                    Get calls <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 rounded-2xl bg-slate-50 border border-slate-200 px-6 py-5">
              <p className="text-slate-600 font-medium">Don't see your vertical? We cover dozens more.</p>
              <a href={company.bookingUrl} target="_blank" rel="noreferrer">
                <Button className="brand-gradient text-white font-bold group">
                  Ask about your vertical
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Audience split */}
      <section className="section-pad bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-8">
          {networkAudience.map((a, i) => (
            <Reveal key={a.title} delay={i * 100}>
              <div className="h-full rounded-3xl border border-slate-200 p-8 md:p-10 card-lift bg-white">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl brand-gradient shadow-lg shadow-blue-600/25">
                  <a.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold text-slate-900">{a.title}</h3>
                <p className="mt-1 text-blue-600 font-semibold">{a.subtitle}</p>
                <div className="mt-6 space-y-3">
                  {a.points.map((pt) => (
                    <div key={pt} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                      <span className="text-slate-700">{pt}</span>
                    </div>
                  ))}
                </div>
                <a href={company.bookingUrl} target="_blank" rel="noreferrer">
                  <Button className="mt-8 brand-gradient text-white font-bold group">
                    {a.cta}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="relative section-pad navy-gradient overflow-hidden">
        <div className="blob h-72 w-72 bg-blue-500 top-0 right-1/4" />
        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white">A network built to perform</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
            {networkStats.map((s, i) => (
              <Reveal key={i} delay={i * 80} className="text-center">
                <AnimatedCounter end={s.value} suffix={s.suffix} className="font-display text-5xl md:text-6xl font-extrabold text-white" />
                <p className="mt-3 text-sm font-semibold uppercase tracking-wide text-blue-200/80">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-slate-50 border border-slate-200 p-10 md:p-16 text-center">
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900">Ready to grow through our networks?</h2>
              <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
                Whether you want more qualified calls or you're a publisher looking to monetize traffic, let's talk.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <a href={company.bookingUrl} target="_blank" rel="noreferrer">
                  <Button size="lg" className="brand-gradient text-white font-bold group w-full sm:w-auto">
                    Schedule a Call
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </a>
                <a href="/contact">
                  <Button size="lg" variant="outline" className="font-bold border-slate-300 hover:border-blue-600 hover:text-blue-600 w-full sm:w-auto">
                    Send us a message
                  </Button>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Networks;
